import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../components/contentwrapper/ContentWrapper";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../utils/withAuth";
import { fetchAllDataByIDAPI, getSingleDataFromDatabase } from "../utils/api";
import { FaFacebookF, FaGoogle } from "react-icons/fa";
import { RiAdvertisementFill } from "react-icons/ri";
import Skeleton from "react-loading-skeleton";
import DateRangePicker from "react-bootstrap-daterangepicker";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
} from "recharts";
import moment from "moment";

const Dashboard = () => {
    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");
    const defaultStart = useMemo(() => moment().startOf("month"), []);
    const defaultEnd = useMemo(() => moment().endOf("day"), []);
    const [dateRange, setDateRange] = useState({
        startDate: defaultStart,
        endDate: defaultEnd,
    });
    const drpRef = useRef(null);
    const [ppc, setPPC] = useState({});
    const [currentTime, setCurrentTime] = useState(new Date());

    const dateLabel = useMemo(() => {
        const s = dateRange.startDate?.format("DD MMM YYYY");
        const e = dateRange.endDate?.format("DD MMM YYYY");
        return `${s} - ${e}`;
    }, [dateRange]);

    // spend cards
    const [isLoading, setIsLoading] = useState(false);
    const [spendData, setSpendData] = useState(null);

    // charts + tables from API
    const [chartData, setChartData] = useState({
        amountByDate: [],
        imprClicksByDate: [],
    });
    const [cpcMetricsByDate, setCpcMetricsByDate] = useState([]);
    const [sourceRows, setSourceRows] = useState([]);
    const [campaignSummaryRows, setCampaignSummaryRows] = useState([]);

    // 1) Always-running clock
    useEffect(() => {
        const t = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // 2) Load PPC basic data
    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/dashboard");
        (async () => {
            try {
                const res = await getSingleDataFromDatabase(
                    `ppc-get-single-data/${ppcid}`
                );
                if (res?.data?.status === 1) setPPC(res.data.ppcdata || {});
                else toast.error(res?.data?.message || "Failed to load PPC data");
            } catch (e) {
                console.error(e);
                toast.error("Network/CORS error while loading PPC data");
            }
        })();
    }, [ppcid, navigate]);

    const formattedTime = currentTime.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    const greeting = () => {
        const h = currentTime.getHours();
        if (h < 12) return "Good Morning";
        if (h < 17) return "Good Afternoon";
        return "Good Evening";
    };

    const defaultSources = [
        { key: "google", label: "Google Ads", icon: <FaGoogle /> },
        { key: "facebook", label: "Facebook Ads", icon: <FaFacebookF /> },
        { key: "others", label: "Others", icon: <RiAdvertisementFill /> },
    ];

    const sections = [
        {
            key: "today",
            label: "Today's Spend",
            accentClass: "border-start border-4 border-primary",
        },
        {
            key: "yesterday",
            label: "Yesterday's Spend",
            accentClass: "border-start border-4 border-success",
        },
        {
            key: "thisMonth",
            label: "This Month Spend",
            accentClass: "border-start border-4 border-warning",
        },
        {
            key: "lastMonth",
            label: "Last Month Spend",
            accentClass: "border-start border-4 border-info",
        },
        {
            key: "overall",
            label: "Total Spend",
            accentClass: "border-start border-4 border-danger",
        },
    ];

    // ---------------------- LOAD DASHBOARD SPEND + METRICS -------------------
    useEffect(() => {
        if (!ppcid) return;
        loadSpend(defaultStart, defaultEnd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const loadSpend = (start = dateRange.startDate, end = dateRange.endDate) => {
        setIsLoading(true);

        const startStr = moment(start).format("YYYY-MM-DD");
        const endStr = moment(end).format("YYYY-MM-DD");

        fetchAllDataByIDAPI(
            `ppc-team-get-dashboard-spend-with-source-name/${ppcid}?start_date=${startStr}&end_date=${endStr}`
        )
            .then((res) => {
                if (res?.data?.status === 1) {
                    const api = res.data.data || {};

                    // ✅ NEW: cards object from API
                    const cards = api.cards || {};

                    const formatRangeLabel = (start, end) => {
                        if (!start && !end) return "";

                        const fmt = (d) =>
                            new Date(d).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            });

                        if (!start || !end) return start || end || "";
                        if (start === end) return fmt(start);
                        return `${fmt(start)} – ${fmt(end)}`;
                    };

                    // ✅ read sections from cards.<key> instead of api.<key>
                    const mapSec = (keyApi) => {
                        const sec = cards[keyApi] || {};
                        return {
                            label: sec.label || "",
                            dateLabel: formatRangeLabel(
                                sec.date_start || null,
                                sec.date_end || null
                            ),
                            total: Number(sec.total) || 0,
                            sources: sec.sources || [],
                        };
                    };

                    // ---- cards data ----
                    setSpendData({
                        today: mapSec("today"),
                        yesterday: mapSec("yesterday"),
                        thisMonth: mapSec("this_month"),
                        lastMonth: mapSec("last_month"),
                        overall: mapSec("overall"),
                    });

                    // ---- charts data ----
                    const charts = api.charts || {};
                    const amountRows = charts.amount_by_date?.rows || [];
                    const metricRows =
                        charts.impressions_clicks_by_date?.rows || [];

                    const shortDate = (d) =>
                        new Date(d).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                        });

                    const amountByDate = amountRows.map((r) => ({
                        date: shortDate(r.date),
                        spend: Number(r.total_spend) || 0,
                    }));

                    const imprClicksByDate = metricRows.map((r) => ({
                        date: shortDate(r.date),
                        impressions: Number(r.impressions) || 0,
                        clicks: Number(r.clicks) || 0,
                    }));

                    setChartData({
                        amountByDate,
                        imprClicksByDate,
                    });

                    // ---- CPC by date (overall) ----
                    const metricsByDateMap = {};
                    metricRows.forEach((r) => {
                        metricsByDateMap[r.date] = {
                            impressions: Number(r.impressions) || 0,
                            clicks: Number(r.clicks) || 0,
                        };
                    });

                    const metricRowsByDate = {};
                    metricRows.forEach((r) => {
                        metricRowsByDate[r.date] = {
                            impressions: Number(r.impressions) || 0,
                            clicks: Number(r.clicks) || 0,
                        };
                    });

                    const rows = amountRows.map((r) => {
                        const m = metricRowsByDate[r.date] || {};
                        const spend = Number(r.total_spend) || 0;
                        const clicks = m.clicks || 0;
                        const impressions = m.impressions || 0;

                        const cpc = clicks > 0 ? Number((spend / clicks).toFixed(2)) : 0;
                        const cpm = impressions > 0 ? Number(((spend / impressions) * 1000).toFixed(2)) : 0;

                        return {
                            period: shortDate(r.date),
                            cpc,
                            cpm,
                            impressions,
                        };
                    });

                    setCpcMetricsByDate(rows);

                    // ---- source performance ----
                    const srcPerf = api.source_performance || [];

                    /* 1️⃣ Convert API data into lookup map */
                    const perfMap = {};
                    srcPerf.forEach((r) => {
                        const key = (r.source_name || "").toLowerCase().trim();
                        perfMap[key] = {
                            impressions: Number(r.impressions) || 0,
                            clicks: Number(r.clicks) || 0,
                            amount: Number(r.amount_spent) || 0,
                        };
                    });

                    /* 2️⃣ Always build table rows from defaultSources */
                    const normalizedSources = defaultSources.map((src) => {
                        const data = perfMap[src.key] || {
                            impressions: 0,
                            clicks: 0,
                            amount: 0,
                        };

                        const impressions = data.impressions;
                        const clicks = data.clicks;
                        const amount = data.amount;

                        const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                        const cpc = clicks > 0 ? amount / clicks : 0;
                        const cpm = impressions > 0 ? (amount / impressions) * 1000 : 0;

                        return {
                            source: src.label,          // ✅ Google Ads / Facebook Ads / Others
                            impressions,
                            clicks,
                            ctr: Number(ctr.toFixed(2)),
                            amount,
                            cpc: Number(cpc.toFixed(2)),
                            cpm: Number(cpm.toFixed(2)),
                        };
                    });

                    setSourceRows(normalizedSources);

                    // ---- campaign summary table ----
                    const summary = api.summary_table || [];
                    const normalizedSummary = summary.map((r, idx) => ({
                        name: r.skill_name || `Campaign ${idx + 1}`,
                        type: r.level || "",
                        source: r.source_name || "",
                        amount: Number(r.amount_spent) || 0,
                        impressions: Number(r.impressions) || 0,
                        clicks: Number(r.clicks) || 0,
                        ctr: Number(r.ctr) || 0,
                        cpc: Number(r.cpc) || 0,
                        cpm: Number(r.cpm) || 0,
                    }));
                    setCampaignSummaryRows(normalizedSummary);
                }
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setIsLoading(false);
            });
    };

    const formatCurrency = (v) =>
        new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(v || 0);

    const getSafeSources = (raw, total) => {
        const map = {};
        raw?.forEach((s) => {
            const key = (s.source_name || "").toLowerCase();
            map[key] = Number(s.spend) || 0;
        });

        return defaultSources.map((src) => {
            const amt = map[src.key] || 0;
            const pct = total > 0 ? ((amt / total) * 100).toFixed(1) : "0.0";
            return { ...src, amount: amt, pct };
        });
    };

    const renderCard = (section) => {
        if (isLoading || !spendData) {
            return (
                <div className="col" key={section.key}>
                    <div className="card p-3" style={{ borderRadius: 16 }}>
                        <Skeleton width={120} height={14} className="mb-2" />
                        <Skeleton width={80} height={26} className="mb-3" />
                        <Skeleton height={10} className="mb-2" />
                        <Skeleton height={10} className="mb-2" />
                        <Skeleton height={10} className="mb-2" />
                    </div>
                </div>
            );
        }

        const data = spendData[section.key];
        if (!data) return null;

        const safeSources = getSafeSources(data.sources, data.total);

        return (
            <div className="col" key={section.key}>
                <div
                    className={`card shadow-sm h-100 ${section.accentClass}`}
                    style={{ borderRadius: 16 }}
                >
                    <div className="p-3 d-flex flex-column h-100">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                                <h6
                                    className="mb-0 text-uppercase"
                                    style={{ fontWeight: 600 }}
                                >
                                    {section.label}
                                </h6>
                                {data.dateLabel && (
                                    <small
                                        style={{ fontSize: 11, color: "#999" }}
                                    >
                                        {data.dateLabel}
                                    </small>
                                )}
                            </div>

                            <h3 style={{ fontSize: 22, fontWeight: 700 }}>
                                {formatCurrency(data.total)}
                            </h3>
                        </div>

                        {safeSources.map((src) => (
                            <div
                                key={src.label}
                                className="d-flex justify-content-between align-items-center mb-1"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        className="rounded-circle d-inline-flex align-items-center justify-content-center"
                                        style={{
                                            width: 26,
                                            height: 26,
                                            background: "#f5f5f5",
                                            fontSize: 12,
                                        }}
                                    >
                                        {src.icon}
                                    </span>
                                    <span style={{ fontSize: 12 }}>
                                        {src.label}
                                    </span>
                                </div>
                                <span>
                                    {formatCurrency(src.amount)} ({src.pct}%)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // --------- small reusable legend with clickable items ----------
    const ChartLegend = ({ chartKey, items, visibility, onToggle }) => {
        return (
            <div className="chart-legend">
                {items.map((item) => {
                    const active = visibility?.[item.key];
                    return (
                        <button
                            key={item.key}
                            type="button"
                            className={`chart-legend-item ${active ? "active" : "inactive"
                                }`}
                            onClick={() => onToggle(chartKey, item.key)}
                        >
                            <span
                                className="chart-legend-dot"
                                style={{ backgroundColor: item.color }}
                            />
                            <span className="chart-legend-label">
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    // ---------------- visibility state for time-series charts -------------
    const [legendVisibility, setLegendVisibility] = useState({
        chart2: { impressions: true, clicks: true },
    });

    const handleToggleLegend = (chartKey, seriesKey) => {
        setLegendVisibility((prev) => ({
            ...prev,
            [chartKey]: {
                ...prev[chartKey],
                [seriesKey]: !prev[chartKey][seriesKey],
            },
        }));
    };

    // ---------------------- source table sorting + totals -----------------
    const [sortConfig, setSortConfig] = useState({
        key: "amount",
        direction: "desc",
    });

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "desc" };
        });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key !== key) return <span className="sort-arrow">↕</span>;
        return (
            <span className="sort-arrow">
                {sortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    const maxValues = useMemo(() => {
        if (!sourceRows.length) {
            return {
                impressions: 0,
                clicks: 0,
                ctr: 0,
                amount: 0,
                cpc: 0,
                cpm: 0,
            };
        }
        return {
            impressions: Math.max(...sourceRows.map((r) => r.impressions)),
            clicks: Math.max(...sourceRows.map((r) => r.clicks)),
            ctr: Math.max(...sourceRows.map((r) => r.ctr)),
            amount: Math.max(...sourceRows.map((r) => r.amount)),
            cpc: Math.max(...sourceRows.map((r) => r.cpc)),
            cpm: Math.max(...sourceRows.map((r) => r.cpm)),
        };
    }, [sourceRows]);

    const sortedSources = useMemo(() => {
        const rows = [...sourceRows];
        const { key, direction } = sortConfig;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av < bv) return direction === "asc" ? -1 : 1;
            if (av > bv) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return rows;
    }, [sortConfig, sourceRows]);

    const clamp01 = (t) => Math.min(1, Math.max(0, t));

    const getHeatStyle = (value, max, theme = "blue") => {
        // Themes: gold | blue | blue2 | mint | pink | rose
        const themes = {
            gold: { hue: 45, sat: 70, lightMin: 30, lightMax: 92 },
            blue: { hue: 215, sat: 75, lightMin: 25, lightMax: 92 },
            blue2: { hue: 200, sat: 75, lightMin: 25, lightMax: 92 },
            mint: { hue: 160, sat: 55, lightMin: 30, lightMax: 95 },
            pink: { hue: 300, sat: 65, lightMin: 35, lightMax: 93 },
            rose: { hue: 330, sat: 55, lightMin: 38, lightMax: 95 },
        };

        const cfg = themes[theme] || themes.blue;

        if (!max || max <= 0) {
            return { background: "transparent", color: "#111827" };
        }

        const t = value === 0 ? 0 : clamp01(value / max);
        const lightness = cfg.lightMax - (cfg.lightMax - cfg.lightMin) * t;

        // ✅ text auto contrast
        const isDark = lightness < 58;
        const textColor = isDark ? "#ffffff" : "#111827";

        return {
            background: `hsl(${cfg.hue}, ${cfg.sat}%, ${lightness}%)`,
            color: textColor,
            fontWeight: 700,

            // ✅ extra visibility on any bg (especially blue/pink)
            textShadow: isDark ? "0 1px 2px rgba(0,0,0,0.35)" : "0 1px 0 rgba(255,255,255,0.6)",
        };
    };

    const formatNumber = (n) => (n || 0).toLocaleString("en-US");
    const formatPercent = (n) => `${(n || 0).toFixed(2)}%`;

    const sourceTotals = useMemo(() => {
        const t = sourceRows.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.amount += row.amount;
                return acc;
            },
            { impressions: 0, clicks: 0, amount: 0 }
        );
        const ctr =
            t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0;
        const cpc = t.clicks > 0 ? t.amount / t.clicks : 0;
        const cpm =
            t.impressions > 0 ? (t.amount / t.impressions) * 1000 : 0;
        return { ...t, ctr, cpc, cpm };
    }, [sourceRows]);

    // -------------------- summary table sorting + totals ------------------
    const [summarySortConfig, setSummarySortConfig] = useState({
        key: "amount",
        direction: "desc",
    });

    const handleSummarySort = (key) => {
        setSummarySortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "desc" };
        });
    };

    const summaryMaxValues = useMemo(() => {
        if (!campaignSummaryRows.length) {
            return {
                amount: 0,
                impressions: 0,
                clicks: 0,
                ctr: 0,
                cpc: 0,
                cpm: 0,
            };
        }
        return {
            amount: Math.max(...campaignSummaryRows.map((r) => r.amount)),
            impressions: Math.max(
                ...campaignSummaryRows.map((r) => r.impressions)
            ),
            clicks: Math.max(...campaignSummaryRows.map((r) => r.clicks)),
            ctr: Math.max(...campaignSummaryRows.map((r) => r.ctr)),
            cpc: Math.max(...campaignSummaryRows.map((r) => r.cpc)),
            cpm: Math.max(...campaignSummaryRows.map((r) => r.cpm)),
        };
    }, [campaignSummaryRows]);

    const renderSummarySortArrow = (key) => {
        if (summarySortConfig.key !== key)
            return <span className="sort-arrow">↕</span>;
        return (
            <span className="sort-arrow">
                {summarySortConfig.direction === "asc" ? "▲" : "▼"}
            </span>
        );
    };

    const sortedCampaignSummaryRows = useMemo(() => {
        const rows = [...campaignSummaryRows];
        const { key, direction } = summarySortConfig;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av < bv) return direction === "asc" ? -1 : 1;
            if (av > bv) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return rows;
    }, [summarySortConfig, campaignSummaryRows]);

    const summaryTotals = useMemo(() => {
        const t = campaignSummaryRows.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.amount += row.amount;
                return acc;
            },
            { impressions: 0, clicks: 0, amount: 0 }
        );
        const ctr =
            t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0;
        const cpc = t.clicks > 0 ? t.amount / t.clicks : 0;
        const cpm =
            t.impressions > 0 ? (t.amount / t.impressions) * 1000 : 0;
        return { ...t, ctr, cpc, cpm };
    }, [campaignSummaryRows]);

    // ============================= RENDER ===============================
    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <ContentWrapper>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="dashboardname m-0">
                        {greeting()}, <span>{ppc?.ppc_name || ""}</span>
                    </h4>
                    <DateRangePicker
                        initialSettings={{
                            startDate: dateRange.startDate.toDate(),
                            endDate: dateRange.endDate.toDate(),
                            autoUpdateInput: false,
                            showDropdowns: true,
                            alwaysShowCalendars: true,
                            locale: { format: "DD MMM YYYY" },
                        }}
                        onApply={(e, picker) => {
                            const start = moment(picker.startDate);
                            const end = moment(picker.endDate);

                            setDateRange({ startDate: start, endDate: end });
                            loadSpend(start, end);
                        }}
                    >
                        <button className="btn btn-sm btndaterange" ref={drpRef} type="button">
                            {dateLabel}
                        </button>
                    </DateRangePicker>
                    <h4 className="m-0" style={{ fontWeight: 600 }}>
                        ⏰ {formattedTime}
                    </h4>
                </div>

                {/* TOP SPEND CARDS */}
                <div className="inv-component mb-2">
                    <div className="row row-cols-1 row-cols-lg-5 g-3">
                        {sections.map((s) => renderCard(s))}
                    </div>
                </div>

                {/* MAIN DASHBOARD BLOCKS */}
                <div className="row g-3" style={{ marginBottom: "55px" }}>
                    {/* 1: Amount spent over time */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Amount spend by Date</h5>
                            </div>
                            {isLoading ? (
                                <Skeleton height={250} />
                            ) : (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={chartData.amountByDate}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fill: "#1F2937" }}
                                        />
                                        <YAxis tick={{ fill: "#1F2937" }} />
                                        <Tooltip
                                            contentStyle={{ color: "#1F2937" }}
                                            labelStyle={{ color: "#1F2937" }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="spend"
                                            stroke="#22c55e"
                                            strokeWidth={2}
                                            dot={{
                                                stroke: "#22c55e",
                                                strokeWidth: 2,
                                            }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* 2: Impressions vs Clicks */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header d-flex justify-content-between align-items-center">
                                <h5>Impressions and Clicks by Date</h5>
                                <ChartLegend
                                    chartKey="chart2"
                                    visibility={legendVisibility.chart2}
                                    onToggle={handleToggleLegend}
                                    items={[
                                        {
                                            key: "impressions",
                                            label: "Impressions",
                                            color: "#0ea5e9",
                                        },
                                        {
                                            key: "clicks",
                                            label: "Clicks",
                                            color: "#dc2626",
                                        },
                                    ]}
                                />
                            </div>

                            {isLoading ? (
                                <Skeleton height={250} />
                            ) : (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={chartData.imprClicksByDate}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fill: "#1F2937" }}
                                        />
                                        <YAxis tick={{ fill: "#1F2937" }} />
                                        <Tooltip />
                                        {legendVisibility.chart2.impressions && (
                                            <Line
                                                dataKey="impressions"
                                                stroke="#0ea5e9"
                                                strokeWidth={2}
                                            />
                                        )}
                                        {legendVisibility.chart2.clicks && (
                                            <Line
                                                dataKey="clicks"
                                                stroke="#dc2626"
                                                strokeWidth={2}
                                            />
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* 5: Summary table */}
                    <div className="col-12">
                        <div className="chart-card summary-table-card">
                            <div className="chart-card-header">
                                <h5>Summary table</h5>
                            </div>

                            <div className="source-table-wrapper">
                                <div className="source-table-scroll">
                                    {isLoading ? (
                                        <Skeleton height={260} />
                                    ) : (
                                        <table className="source-table">
                                            <thead>
                                                <tr>
                                                    <th>Campaign name</th>
                                                    <th>Campaign type</th>
                                                    <th>Source</th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "amount"
                                                            )
                                                        }
                                                    >
                                                        Amount spend{" "}
                                                        {renderSummarySortArrow(
                                                            "amount"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "impressions"
                                                            )
                                                        }
                                                    >
                                                        Impressions{" "}
                                                        {renderSummarySortArrow(
                                                            "impressions"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "clicks"
                                                            )
                                                        }
                                                    >
                                                        Clicks{" "}
                                                        {renderSummarySortArrow(
                                                            "clicks"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "ctr"
                                                            )
                                                        }
                                                    >
                                                        CTR{" "}
                                                        {renderSummarySortArrow(
                                                            "ctr"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "cpc"
                                                            )
                                                        }
                                                    >
                                                        CPC{" "}
                                                        {renderSummarySortArrow(
                                                            "cpc"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSummarySort(
                                                                "cpm"
                                                            )
                                                        }
                                                    >
                                                        CPM{" "}
                                                        {renderSummarySortArrow(
                                                            "cpm"
                                                        )}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedCampaignSummaryRows.map(
                                                    (row) => (
                                                        <tr key={row.name}>
                                                            <td>{row.name}</td>
                                                            <td>{row.type}</td>
                                                            <td>
                                                                {row.source}
                                                            </td>
                                                            <td className="heat-cell" style={getHeatStyle(row.amount, summaryMaxValues.amount, "gold")}>
                                                                {formatCurrency(row.amount)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.impressions, summaryMaxValues.impressions, "blue")}>
                                                                {formatNumber(row.impressions)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.clicks, summaryMaxValues.clicks, "blue2")}>
                                                                {formatNumber(row.clicks)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.ctr, summaryMaxValues.ctr, "mint")}>
                                                                {formatPercent(row.ctr)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.cpc, summaryMaxValues.cpc, "pink")}>
                                                                {formatCurrency(row.cpc)}
                                                            </td>

                                                            <td className="heat-cell" style={getHeatStyle(row.cpm, summaryMaxValues.cpm, "rose")}>
                                                                {formatCurrency(row.cpm)}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={3}>
                                                        Grand total
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            summaryTotals.amount
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            summaryTotals.impressions
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            summaryTotals.clicks
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatPercent(
                                                            summaryTotals.ctr
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            summaryTotals.cpc
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            summaryTotals.cpm
                                                        )}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3: Source performance table */}
                    <div className="col-md-6">
                        <div className="chart-card source-table-card">
                            <div className="chart-card-header">
                                <h5>Source performance</h5>
                            </div>

                            <div className="source-table-wrapper">
                                <div className="source-table-scroll">
                                    {isLoading ? (
                                        <Skeleton height={220} />
                                    ) : (
                                        <table className="source-table">
                                            <thead>
                                                <tr>
                                                    <th>Source</th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort(
                                                                "impressions"
                                                            )
                                                        }
                                                    >
                                                        Impressions{" "}
                                                        {renderSortArrow(
                                                            "impressions"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("clicks")
                                                        }
                                                    >
                                                        Clicks{" "}
                                                        {renderSortArrow(
                                                            "clicks"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("ctr")
                                                        }
                                                    >
                                                        CTR{" "}
                                                        {renderSortArrow("ctr")}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("amount")
                                                        }
                                                    >
                                                        Amount spend{" "}
                                                        {renderSortArrow(
                                                            "amount"
                                                        )}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("cpc")
                                                        }
                                                    >
                                                        CPC{" "}
                                                        {renderSortArrow("cpc")}
                                                    </th>
                                                    <th
                                                        className="sortable-th"
                                                        onClick={() =>
                                                            handleSort("cpm")
                                                        }
                                                    >
                                                        CPM{" "}
                                                        {renderSortArrow("cpm")}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedSources.map((row) => (
                                                    <tr key={row.source}>
                                                        <td>{row.source}</td>

                                                        {/* ✅ Impressions */}
                                                        <td className="heat-cell" style={getHeatStyle(row.impressions, maxValues.impressions, "blue")}>
                                                            {formatNumber(row.impressions)}
                                                        </td>

                                                        {/* ✅ Clicks */}
                                                        <td className="heat-cell" style={getHeatStyle(row.clicks, maxValues.clicks, "blue2")}>
                                                            {formatNumber(row.clicks)}
                                                        </td>

                                                        {/* ✅ CTR */}
                                                        <td className="heat-cell" style={getHeatStyle(row.ctr, maxValues.ctr, "mint")}>
                                                            {formatPercent(row.ctr)}
                                                        </td>

                                                        {/* ✅ Amount spent */}
                                                        <td className="heat-cell" style={getHeatStyle(row.amount, maxValues.amount, "gold")}>
                                                            {formatCurrency(row.amount)}
                                                        </td>

                                                        {/* ✅ CPC */}
                                                        <td className="heat-cell" style={getHeatStyle(row.cpc, maxValues.cpc, "pink")}>
                                                            {formatCurrency(row.cpc)}
                                                        </td>

                                                        {/* ✅ CPM */}
                                                        <td className="heat-cell" style={getHeatStyle(row.cpm, maxValues.cpm, "rose")}>
                                                            {formatCurrency(row.cpm)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td>Grand total</td>
                                                    <td>
                                                        {formatNumber(
                                                            sourceTotals.impressions
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatNumber(
                                                            sourceTotals.clicks
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatPercent(
                                                            sourceTotals.ctr
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            sourceTotals.amount
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            sourceTotals.cpc
                                                        )}
                                                    </td>
                                                    <td>
                                                        {formatCurrency(
                                                            sourceTotals.cpm
                                                        )}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 4: CPC by date (overall) */}
                    <div className="col-md-6">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <h5>Cost per click by Date</h5>
                            </div>
                            {isLoading ? (
                                <Skeleton height={260} />
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={cpcMetricsByDate}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="period" tick={{ fill: "#1F2937" }} />

                                        {/* Left axis: CPC + CPM */}
                                        <YAxis yAxisId="left" tick={{ fill: "#1F2937" }} />

                                        {/* Right axis: Impressions */}
                                        <YAxis yAxisId="right" orientation="right" tick={{ fill: "#1F2937" }} />

                                        <Tooltip
                                            formatter={(value, name) => {
                                                if (name === "Impressions") return [Number(value || 0).toLocaleString("en-IN"), name];
                                                return [new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(value || 0), name];
                                            }}
                                        />

                                        <Legend verticalAlign="top" align="left" iconType="circle" wrapperStyle={{ paddingBottom: 8 }} />

                                        <Bar yAxisId="left" dataKey="cpc" fill="#2563eb" name="Avg CPC" />
                                        <Bar yAxisId="left" dataKey="cpm" fill="#f59e0b" name="CPM" />
                                        <Bar yAxisId="right" dataKey="impressions" fill="#0ea5e9" name="Impressions" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(Dashboard);
